/* eslint-disable @typescript-eslint/no-explicit-any */
import type { FC } from 'react'
import React, { useMemo } from 'react'

import type { DividerProps } from './types'
import { createUseStyledComponent } from '../../../hooks'
import type { Theme } from '../../../theme-provider/types'
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui'
import { getSafeTransparentize } from '../../../utils/colors'
import extractStringNodes from '../../../utils/extract-string-nodes'
import { EMPTY_OBJECT, omit } from '../../../utils/object'
import { resolveColor } from '../../../utils/styled-props'
import { BoxPrivate } from '../box/box'
import { TextPrivate } from '../text/text'

const baseReset: any = {
  margin: 0, // Reset browser default style.
  border: 'none',
  flexShrink: 0
}

const useStyledComponent = createUseStyledComponent(['hr'], 'csg-divider')
const Divider: FC<DividerProps> = React.forwardRef<
  HTMLHRElement,
  DividerProps & { theme?: Theme }
>(function Divider(props, ref) {
  const {
    absolute = false,
    as: component = 'hr',
    flexItem = false,
    orientation = 'horizontal',
    light = false,
    role = component !== 'hr' ? 'separator' : undefined,
    color: colorProps,
    bg,
    backgroundColor,
    height,
    resetCss = EMPTY_OBJECT,
    width,
    children,
    ...rest
  } = props
  const theme = useSafeThemeUI(props)
  const _color = colorProps || bg || backgroundColor
  const Component = useStyledComponent(component)
  const stringNodes = extractStringNodes(
    React.Children.toArray(children).filter(i => typeof i === 'string')
  )

  const _resetCss = useMemo(
    () => ({
      ...omit(baseReset, Object.keys(resetCss as any)),
      ...resetCss // we want to pass this reset through to create-component to use it to filter out reset when it is null or undefined or empty string
    }),
    [resetCss]
  )

  const colors = useMemo(() => {
    const color = light
      ? getSafeTransparentize(
          0.5,
          resolveColor(theme, _color ? _color : 'neutral')
        )
      : resolveColor(theme, _color ? _color : 'neutral')
    return {
      bg: color,
      border: `1px solid ${color}`
    }
  }, [theme, _color, light])

  return stringNodes.length > 0 && orientation === 'horizontal' ? (
    <BoxPrivate
      as="fieldset"
      __css={{
        textAlign: 'center',
        margin: '0 auto',
        borderTop: colors.border,
        width: width ?? '50%'
      }}
    >
      <TextPrivate
        as="legend"
        lhCrop="body"
        __css={{
          color: 'text',
          emPaddingX: 'xs'
        }}
      >
        {stringNodes}
      </TextPrivate>
    </BoxPrivate>
  ) : (
    <Component
      ref={ref}
      className="csg-tui-divider"
      role={role}
      resetCss={_resetCss}
      __css={{
        bg: colors.bg,
        ...(absolute
          ? { position: 'absolute', bottom: 0, left: 0, width: width || '100%' }
          : {}),
        ...(orientation === 'vertical'
          ? { height: height ? height : '1em', width: width || '1px' }
          : { width: width || '100%', height: height || '1px' }),
        ...(flexItem
          ? { alignSelf: 'stretch', height: height ? height : 'auto' }
          : {})
      }}
      {...rest}
    />
  )
})

export default Divider
